import { useEffect, useRef } from "react";

/**
 * useRealtime — opens a WS connection to /api/ws and invokes `onEvent` for each
 * server-pushed message. Automatically reconnects with exponential backoff
 * (max ~32s). Uses HttpOnly cookie for authentication (no JWT in URL) —
 * falls back to legacy ?token= query only if `localStorage.akm_ws_legacy=1`.
 *
 * Server events: connected | reservation.created | reservation.updated |
 *                reservation.deleted | room.status_changed | sales_lead.created
 */
export default function useRealtime(onEvent) {
    const wsRef = useRef(null);
    const handlerRef = useRef(onEvent);
    handlerRef.current = onEvent;

    useEffect(() => {
        let retry = 0;
        let stopped = false;
        let pingTimer = null;
        let reconnectTimer = null;

        const connect = () => {
            const base = process.env.REACT_APP_BACKEND_URL || "";
            const wsBase = base.replace(/^http/, "ws");
            // HttpOnly cookie is sent automatically by the browser for same-origin.
            // Legacy fallback: only if opted-in via localStorage flag.
            let url = `${wsBase}/api/ws`;
            const legacy = localStorage.getItem("akm_ws_legacy") === "1";
            if (legacy) {
                const token = localStorage.getItem("akm_token");
                if (token) url += `?token=${encodeURIComponent(token)}`;
            }
            let ws;
            try { ws = new WebSocket(url); }
            catch { return; }
            wsRef.current = ws;
            ws.onopen = () => { retry = 0; };
            ws.onmessage = (e) => {
                if (e.data === "pong") return;
                try {
                    const msg = JSON.parse(e.data);
                    handlerRef.current?.(msg);
                } catch { /* ignore */ }
            };
            ws.onclose = (evt) => {
                if (stopped) return;
                // Auth failure (4401) — don't retry endlessly to avoid console spam
                if (evt && evt.code === 4401) {
                    retry = 6;
                    return;
                }
                retry = Math.min(retry + 1, 6);
                const delay = 500 * Math.pow(2, retry);
                reconnectTimer = setTimeout(connect, delay);
            };
            ws.onerror = () => { try { ws.close(); } catch {} };
            // Keep-alive ping
            pingTimer = setInterval(() => {
                if (ws.readyState === 1) {
                    try { ws.send("ping"); } catch { /* ignore */ }
                }
            }, 25000);
        };

        connect();
        return () => {
            stopped = true;
            if (pingTimer) clearInterval(pingTimer);
            if (reconnectTimer) clearTimeout(reconnectTimer);
            try { wsRef.current?.close(); } catch { /* ignore */ }
        };
    }, []);
}
